import React, { useCallback, useMemo } from 'react'
import { Polygon, PolygonProps, Tooltip } from 'react-leaflet'
import { Typography } from 'components'
import { LeafletEventHandlerFnMap } from 'leaflet'
import MapPopup, { PopupProps } from './MapPopup/MapPopup'
import { useMapContext } from 'components/MapV2/MapProvider'

export type PolygonItem = PolygonProps & { tooltipText?: string; id: number | string }

export type PolygonsProps = {
  polygons: PolygonItem[]
  pmIgnore?: boolean
}

export type MapPolygonProps = {
  polygonPopup?: PopupProps
  polygons?: PolygonItem[]
  onPolygonSelect?: (polygon: PolygonItem) => void
}

export const Polygons: React.FC<React.PropsWithChildren<PolygonsProps>> = ({
  polygons,
  pmIgnore = true,
}) => {
  return (
    polygons &&
    polygons.length > 0 &&
    polygons.map((polygon) => {
      return <PolygonInternal key={polygon.id} polygon={polygon} pmIgnore={pmIgnore} />
    })
  )
}

type PolygonInternalProps = {
  polygon: PolygonItem
  pmIgnore?: boolean
}

export const PolygonInternal: React.FC<React.PropsWithChildren<PolygonInternalProps>> = (props) => {
  const { polygon, pmIgnore } = props

  const { onPolygonSelect: onSelect, polygonPopup } = useMapContext()

  const handleOnSelect = useCallback(
    (polygon: PolygonItem) => {
      onSelect?.(polygon)
    },
    [onSelect],
  )

  const eventHandlers: LeafletEventHandlerFnMap = useMemo(
    () => ({
      click: () => handleOnSelect?.(polygon),
    }),
    [polygon, handleOnSelect],
  )

  return (
    <Polygon key={polygon.id} {...polygon} pathOptions={{ pmIgnore }} eventHandlers={eventHandlers}>
      {pmIgnore && polygon.tooltipText && (
        <Tooltip sticky>
          <div style={{ width: 150 }}>
            <Typography align="center">{polygon.tooltipText}</Typography>
          </div>
        </Tooltip>
      )}

      {pmIgnore && polygonPopup && <MapPopup {...polygonPopup} />}

      {pmIgnore && polygon.children ? polygon.children : null}
    </Polygon>
  )
}
